import { ITopicPair } from "src/app/nlp/~store/models/TopicPair";
import { NlpButtonType } from "src/app/nlp/~store/models/Answer";
import { TopicPairParameterJsonKey, TopicPairsTree } from "../models/common";
import {
    extractAllTopicPairNextTriggers,
    extractTopicPairButtons,
    extractTopicPairParameterJson,
} from "./graphDataCreation";
import { NEXT_TRIGGER_TYPE_BACK } from "../constants/common";

export const flattenTree = (tree: TopicPairsTree): TopicPairsTree[] => {
    const result: TopicPairsTree[] = [];

    const traverse = (node: TopicPairsTree) => {
        result.push(node);
        for (const child of node.children) {
            traverse(child);
        }
    };

    traverse(tree);
    return result;
};

export const buildTopicPairsTree = (
    topicPairs: ITopicPair[],
    rootTopicPairId: number
): TopicPairsTree | undefined => {
    const rootPair = topicPairs.find(
        (pair) => pair.topicPairId === rootTopicPairId
    );

    if (!rootPair) {
        return undefined;
    }

    const tree: TopicPairsTree = {
        id: rootPair.topicPairId.toString(),
        pair: rootPair,
        children: [],
    };

    buildTree(tree, topicPairs);

    return tree;
};

const buildTree = (tree: TopicPairsTree, topicPairs: ITopicPair[]): void => {
    const queue: TopicPairsTree[] = [tree];

    while (queue.length > 0) {
        const currentTree = queue.shift() as TopicPairsTree;

        const buttonsTrees = addChildToTreeFromButtons(
            topicPairs,
            tree,
            currentTree
        );

        queue.push(...buttonsTrees);

        const nextTriggersTrees = addChildrenToTreeFromNextTriggers(
            topicPairs,
            tree,
            currentTree
        );

        queue.push(...nextTriggersTrees);
    }
};

const addChildToTreeFromButtons = (
    topicPairs: ITopicPair[],
    tree: TopicPairsTree,
    currentTree: TopicPairsTree
) => {
    const trees: TopicPairsTree[] = [];
    extractTopicPairButtons(currentTree.pair).forEach((button) => {
        let targetPair: ITopicPair | undefined;

        if (button.type === NlpButtonType.Trigger) {
            targetPair = findTargetPair(topicPairs, button.value);
        }

        trees.push(...addChildToTree(tree, currentTree, targetPair));
    });
    return trees;
};

const addChildrenToTreeFromNextTriggers = (
    topicPairs: ITopicPair[],
    tree: TopicPairsTree,
    currentTree: TopicPairsTree
) => {
    const trees: TopicPairsTree[] = [];
    extractAllTopicPairNextTriggers(currentTree.pair).forEach((nextTrigger) => {
        const targetPair = findTargetPair(topicPairs, nextTrigger);
        trees.push(...addChildToTree(tree, currentTree, targetPair));
    });
    return trees;
};

const addChildToTree = (
    tree: TopicPairsTree,
    currentTree: TopicPairsTree,
    targetPair?: ITopicPair
): TopicPairsTree[] => {
    const trees: TopicPairsTree[] = [];
    if (targetPair && !isPairInCurrentTree(currentTree, targetPair)) {
        const childTree: TopicPairsTree = {
            id: currentTree.id + "-" + targetPair.topicPairId.toString(),
            pair: targetPair,
            children: [],
        };
        currentTree.children.push(childTree);
        trees.push(childTree);
    }
    return trees;
};

const findTargetPair = (
    topicPairs: ITopicPair[],
    triggerName: string
): ITopicPair | undefined => {
    return topicPairs.find(
        (pair) =>
            pair.triggerName === triggerName &&
            // skip pairs whose next trigger leads to the start dialog
            extractTopicPairParameterJson(
                pair,
                TopicPairParameterJsonKey.NextTriggerType
            ) !== NEXT_TRIGGER_TYPE_BACK
    );
};

const isPairInTree = (tree: TopicPairsTree, pair: ITopicPair): boolean => {
    if (tree.pair.topicPairId === pair.topicPairId) {
        return true;
    }
    for (const child of tree.children) {
        if (isPairInTree(child, pair)) {
            return true;
        }
    }
    return false;
};

const isPairInCurrentTree = (tree: TopicPairsTree, pair: ITopicPair): boolean =>
    tree.id.split("-").includes(pair.topicPairId + "");
